import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logEvent } from '../lib/activityLogger';
import { ensureFreshSession, noteAppWoke } from '../lib/sessionRecovery';
import { withSupabaseQueryTimeout } from '../lib/supabaseTimeout';
import { resolveTenantSlug, setStaffTenantHeader, tenantPortalUrl } from '../lib/tenant';
import { associationService } from '../services/associationService';
import { useHOA } from './HOAContext';

// Production security check
if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_ENV === 'production') {
  // In production, auth must come from Supabase, not hardcoded values
  console.warn('SECURITY: Running in production mode. Ensure Supabase Auth is active.');
}

const AuthContext = createContext(null);

export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGEMENT: 'management',
  BOARD: 'board',
  MEMBER: 'member'
};

export const getPortalRole = (roles = []) => {
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('management')) return 'management';
  if (roles.includes('board')) return 'board';
  if (roles.includes('member')) return 'member';
  return null;
};

// platform_owners is deny-all under RLS, so reading the table always returned
// nothing; is_platform_staff() (SECURITY DEFINER) answers for the caller only.
const fetchIsPlatformOwner = async () => {
  const { data, error } = await withSupabaseQueryTimeout(
    () => supabase.rpc('is_platform_staff'),
    'Checking Atrionos staff access',
    20000
  );
  if (error) throw error;
  return data === true;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTier, setCurrentTier] = useState(null);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [impersonating, setImpersonating] = useState(null);
  // 'pending' until the signed-in user is matched to this tab's association;
  // 'ready' once they belong here (or are Super Admin with the header set);
  // 'mismatch' when a non-staff account signs in on another association's address.
  const [tenantStatus, setTenantStatus] = useState('pending');
  const [tenantMismatch, setTenantMismatch] = useState(null);
  const { hoa, isLoading: hoaLoading } = useHOA();
  const authInitialized = useRef(false);
  const authMutationInProgress = useRef(false);
  const authTaskSequence = useRef(0);
  const portalAccessUserId = useRef(null);
  const [tabVisibility, setTabVisibility] = useState(() => {
    // Bump this version whenever defaults change to force-clear stale localStorage.
    const TAB_VISIBILITY_VERSION = 5;

    const defaults = {
      member: {
        dashboard: true, documents: true, messages: true, inquiry: true, acc: true, events: true, directory: true, profile: true,
        payments: false, settings: false,
      },
      board: {
        dashboard: true, documents: true, events: true, members: true, profile: true,
        financials: false, meetings: false, voting: false, communications: false, settings: false,
      },
      management: {
        dashboard: true, documents: true, accReview: true, members: true, inquiries: true, events: true, profile: true,
      },
      admin: {
        dashboard: true, documents: true, events: true, users: true, profile: true, settings: true,
        payments: true,
        providers: false, content: false, notifications: false,
      },
    };

    const savedVersion = parseInt(localStorage.getItem('tabVisibilityVersion') || '0', 10);
    if (savedVersion < TAB_VISIBILITY_VERSION) {
      // Stale — clear and use fresh defaults
      localStorage.removeItem('tabVisibility');
      localStorage.setItem('tabVisibilityVersion', String(TAB_VISIBILITY_VERSION));
      return defaults;
    }

    const saved = localStorage.getItem('tabVisibility');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Merge: parsed values override defaults (user admin choices persist),
        // but defaults fill in any tabs that didn't exist in the stored version.
        return {
          member:     { ...defaults.member,     ...parsed.member },
          board:      { ...defaults.board,      ...parsed.board },
          management: { ...defaults.management, ...parsed.management },
          admin:      { ...defaults.admin,      ...parsed.admin },
        };
      } catch (e) { /* ignore */ }
    }
    return defaults;
  });

  const fetchUserProfile = async (supabaseUser) => {
    try {
      const { data: profile, error: profileError } = await withSupabaseQueryTimeout(
        () => supabase
          .from('user_profiles')
          .select('*')
          .eq('id', supabaseUser.id)
          .single(),
        'Loading portal profile',
        20000
      );
      if (profileError?.code === 'PGRST116') return null;
      if (profileError) throw profileError;

      const { data: rolesData, error: rolesError } = await withSupabaseQueryTimeout(
        () => supabase
          .from('user_roles')
          .select('role_id')
          .eq('user_id', supabaseUser.id)
          .eq('is_active', true),
        'Loading portal roles',
        20000
      );
      if (rolesError) throw rolesError;



      let roles = [];
      if (rolesData && rolesData.length > 0) {
        const roleIds = rolesData.map(r => r.role_id);
        const { data: roleNames, error: roleNamesError } = await withSupabaseQueryTimeout(
          () => supabase
            .from('roles')
            .select('name')
            .in('id', roleIds),
          'Loading portal role names',
          20000
        );
        if (roleNamesError) throw roleNamesError;
        roles = roleNames ? roleNames.map(r => r.name) : [];
      }

      // Atrionos staff may have no role row anywhere; they act as admin.
      if (profile && roles.length === 0 && await fetchIsPlatformOwner()) {
        roles = ['admin'];
      }

      if (!profile || profile.is_active === false || roles.length === 0) return null;

      return {
        id: profile.id,
        email: profile.email,
        name: profile.full_name,
        full_name: profile.full_name,
        first_name: profile.first_name,
        last_name: profile.last_name,
        roles: roles,
        portalRole: getPortalRole(roles),
        address: profile.address,
        phone: profile.phone,
        hoa_id: profile.hoa_id,
        home_hoa_id: profile.hoa_id,
        is_acc_member: profile.is_acc_member || false,
      };
    } catch (err) {
      console.error('Error fetching user profile:', err);
      throw err;
    }
  };

  const recordPortalAccess = useCallback((profile, method) => {
    if (!profile?.id || portalAccessUserId.current === profile.id) return;
    portalAccessUserId.current = profile.id;
    logEvent(
      'login',
      {
        method,
        role: profile.portalRole,
        actor_roles: [profile.portalRole].filter(Boolean),
      },
      { respectImpersonation: false }
    ).then(
      (recorded) => {
        if (!recorded && portalAccessUserId.current === profile.id) {
          portalAccessUserId.current = null;
        }
      },
      () => {
        if (portalAccessUserId.current === profile.id) {
          portalAccessUserId.current = null;
        }
      }
    );
  }, []);

  useEffect(() => {
    let isMounted = true;
    const queuedAuthWork = new Set();

    const clearAuthenticatedUser = () => {
      setUser(null);
      setIsAuthenticated(false);
      setIsPlatformOwner(false);
      setCurrentTier(null);
      setImpersonating(null);
      portalAccessUserId.current = null;
      localStorage.removeItem('impersonating');
    };

    const loadAuthenticatedUser = async (
      sessionUser,
      { logLogin = false, loginMethod = 'auth_event', taskId } = {}
    ) => {
      const profile = await fetchUserProfile(sessionUser);
      if (!isMounted || (taskId && taskId !== authTaskSequence.current)) return;

      if (!profile) {
        await supabase.auth.signOut();
        if (isMounted) clearAuthenticatedUser();
        return;
      }

      const isOwner = await fetchIsPlatformOwner();
      if (!isMounted || (taskId && taskId !== authTaskSequence.current)) return;

      setIsPlatformOwner(isOwner);
      setUser(profile);
      setIsAuthenticated(true);
      if (logLogin) recordPortalAccess(profile, loginMethod);
    };

    const queueAuthenticatedUserLoad = (sessionUser) => {
      const taskId = ++authTaskSequence.current;
      const timeoutId = window.setTimeout(() => {
        queuedAuthWork.delete(timeoutId);
        if (!isMounted) return;

        loadAuthenticatedUser(sessionUser, {
          logLogin: true,
          loginMethod: 'auth_event',
          taskId,
        }).then(null, (error) => {
          if (!isMounted || taskId !== authTaskSequence.current) return;
          console.warn('[Auth] Deferred sign-in recovery failed:', error?.message || error);
        });
      }, 0);
      queuedAuthWork.add(timeoutId);
    };

    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (session?.user) {
          await loadAuthenticatedUser(session.user, {
            logLogin: true,
            loginMethod: 'session_restore',
          });
        }
      } catch (error) {
        console.warn('[Auth] Initial session restore failed:', error?.message || error);
      } finally {
        if (isMounted) {
          authInitialized.current = true;
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    // Supabase can deadlock if any async client call runs inside this callback.
    // Keep the listener synchronous and defer profile work until its auth lock is released.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        event === 'SIGNED_IN'
        && session?.user
        && authInitialized.current
        && !authMutationInProgress.current
      ) {
        queueAuthenticatedUserLoad(session.user);
      } else if (event === 'SIGNED_OUT') {
        authTaskSequence.current += 1;
        clearAuthenticatedUser();
      }
    });

    return () => {
      isMounted = false;
      authTaskSequence.current += 1;
      queuedAuthWork.forEach((timeoutId) => window.clearTimeout(timeoutId));
      queuedAuthWork.clear();
      subscription?.unsubscribe();
    };
  }, [recordPortalAccess]);
  useEffect(() => {
    let isMounted = true;
    let lastRecoveryAt = 0;

    const recoverSession = async (reason) => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

      const now = Date.now();
      if (now - lastRecoveryAt < 30000) return;
      lastRecoveryAt = now;
      noteAppWoke();

      try {
        const session = await ensureFreshSession({ force: true, label: `Restoring portal after ${reason}` });
        if (!isMounted) return;
        if (!session?.user) {
          setUser(null);
          setIsAuthenticated(false);
          setIsPlatformOwner(false);
          setCurrentTier(null);
          return;
        }

        const profile = await fetchUserProfile(session.user);
        if (!isMounted) return;

        if (profile) {
          setUser(profile);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.warn('[Auth] Session wake recovery failed:', error?.message || error);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') recoverSession('tab wake');
    };
    const handleFocus = () => recoverSession('window focus');
    const handleOnline = () => recoverSession('network reconnect');

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);

    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
    // fetchUserProfile is intentionally used as the current auth profile loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    setIsLoading(true);
    authMutationInProgress.current = true;
    portalAccessUserId.current = null;
    setImpersonating(null);
    localStorage.removeItem('impersonating');
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new Error(error.message);

      if (data?.user) {
        const profile = await fetchUserProfile(data.user);
        if (!profile) {
          await supabase.auth.signOut();
          setUser(null);
          setIsAuthenticated(false);
          setIsPlatformOwner(false);
          throw new Error('Portal profile is not active or has no assigned role.');
        }

        const isOwner = await fetchIsPlatformOwner();
        setIsPlatformOwner(isOwner);
        setUser(profile);
        setIsAuthenticated(true);
        recordPortalAccess(profile, 'email');
        // Super Admin: a fresh sign-in lands in the association looked at last.
        if (isOwner) {
          associationService.listAssociations().then((list) => {
            const last = list.find((a) => a.is_last_visited && a.is_active);
            if (last && last.slug !== resolveTenantSlug()) return associationService.switchTo(last.slug);
            return null;
          }).then(null, (e) => console.warn('[Auth] Last-association landing skipped:', e?.message || e));
        }
      }
    } finally {
      authMutationInProgress.current = false;
      setIsLoading(false);
    }
  };

  const logout = async () => {
    authMutationInProgress.current = true;
    try {
      await supabase.auth.signOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsPlatformOwner(false);
      setCurrentTier(null);
      setImpersonating(null);
      portalAccessUserId.current = null;
      localStorage.removeItem('impersonating');
      // Do NOT clear tabVisibility on logout
    } finally {
      authMutationInProgress.current = false;
    }
  };

  const hasRole = (requiredRole) => {
    if (!user) return false;

    const roleHierarchy = {
      [USER_ROLES.ADMIN]: 4,
      [USER_ROLES.MANAGEMENT]: 3,
      [USER_ROLES.BOARD]: 2,
      [USER_ROLES.MEMBER]: 1
    };

    const userPortalRole = user.portalRole || getPortalRole(user.roles || [user.role]);
    return roleHierarchy[userPortalRole] >= roleHierarchy[requiredRole];
  };

  const getEffectiveRole = () => {
    if (!user) return null;
    return currentTier || user.portalRole || getPortalRole(user.roles || [user.role]);
  };

  const switchTier = (tier) => {
    const userPortalRole = user?.portalRole || getPortalRole(user?.roles || [user?.role]);
    if (!isPlatformOwner && userPortalRole !== USER_ROLES.ADMIN) {
      console.warn('[Auth] switchTier blocked: caller is not a platform owner or admin');
      return;
    }
    if (userPortalRole === USER_ROLES.ADMIN) {
      setCurrentTier(tier);
    }
  };

  const resetTier = () => {
    const userPortalRole = user?.portalRole || getPortalRole(user?.roles || [user?.role]);
    if (!isPlatformOwner && userPortalRole !== USER_ROLES.ADMIN) {
      console.warn('[Auth] switchTier blocked: caller is not a platform owner or admin');
      return;
    }
    setCurrentTier(null);
  };

  const isAdminViewingOtherTier = () => {
    const userRole = user?.portalRole || getPortalRole(user?.roles || [user?.role]);
    return (isPlatformOwner === true || userRole === USER_ROLES.ADMIN) && currentTier && currentTier !== userRole;
  };

  const startImpersonating = (targetUser) => {
    // Persist synchronously so any logEvent during this session is attributed correctly.
    try { localStorage.setItem('impersonating', JSON.stringify(targetUser)); } catch (_) {}
    setImpersonating(targetUser);
    // Marker event is attributed to the ADMIN (respectImpersonation:false), clearly tagged.
    logEvent(
      'impersonation_started',
      { impersonated_as: targetUser.id, impersonated_as_name: targetUser.name || targetUser.full_name || null, note: 'admin_view_as' },
      { respectImpersonation: false }
    );
  };

  const stopImpersonating = () => {
    if (impersonating) {
      logEvent(
        'impersonation_ended',
        { restored_from: impersonating.id, note: 'admin_returned' },
        { respectImpersonation: false }
      );
    }
    try { localStorage.removeItem('impersonating'); } catch (_) {}
    setImpersonating(null);
  };

  useEffect(() => {
    if (impersonating) {
      localStorage.setItem('impersonating', JSON.stringify(impersonating));
    } else {
      localStorage.removeItem('impersonating');
    }
  }, [impersonating]);

  // Match the signed-in account to this tab's association (from the web address).
  // Super Admin: act inside it; the tenant header goes out only when it is not
  // their home association, so their home portal behaves exactly as before.
  // Everyone else: their own association only; on another association's address
  // they are signed out and pointed home (WrongAssociation).
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setStaffTenantHeader(null);
      setTenantStatus('pending');
      return undefined;
    }
    if (hoaLoading || !hoa) return undefined;

    const homeHoaId = user.home_hoa_id || user.hoa_id;

    if (isPlatformOwner) {
      setStaffTenantHeader(hoa.id !== homeHoaId ? hoa.slug : null);
      if (user.hoa_id !== hoa.id) {
        setUser((prev) => (prev ? { ...prev, hoa_id: hoa.id } : prev));
      }
      setTenantMismatch(null);
      setTenantStatus('ready');
      return undefined;
    }

    setStaffTenantHeader(null);
    if (homeHoaId === hoa.id) {
      setTenantMismatch(null);
      setTenantStatus('ready');
      return undefined;
    }

    let cancelled = false;
    setTenantStatus('mismatch');
    (async () => {
      let home = null;
      try {
        const { data } = await supabase.from('hoas').select('name, slug').eq('id', homeHoaId).maybeSingle();
        home = data;
      } catch (_) {
        home = null;
      }
      if (cancelled) return;
      setTenantMismatch({
        currentName: hoa.name,
        homeName: home?.name || null,
        homeUrl: home?.slug ? tenantPortalUrl(home.slug, '/portal') : null,
      });
      // scope 'local': end only this browser's session on this address. The default
      // ('global') would also sign the resident out of their own portal and phone.
      await supabase.auth.signOut({ scope: 'local' });
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, user, hoa, hoaLoading, isPlatformOwner]);

  const clearTenantMismatch = () => setTenantMismatch(null);

  const effectiveRole = impersonating?.role ?? currentTier ?? user?.portalRole ?? getPortalRole(user?.roles || [user?.role]) ?? null;
  const effectivePermission = impersonating?.permission_level ?? user?.permissionLevel ?? 0;

  const updateTabVisibility = (tier, tabId, visible) => {
    setTabVisibility(prev => {
      const updated = {
        ...prev,
        [tier]: { ...prev[tier], [tabId]: visible }
      };
      localStorage.setItem('tabVisibility', JSON.stringify(updated));
      localStorage.setItem('tabVisibilityVersion', '5');
      return updated;
    });
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    hasRole,
    getEffectiveRole,
    switchTier,
    resetTier,
    currentTier,
    isAdminViewingOtherTier: isAdminViewingOtherTier(),
    tabVisibility,
    updateTabVisibility,
    isPlatformOwner,
    impersonating,
    startImpersonating,
    stopImpersonating,
    effectiveRole,
    effectivePermission,
    tenantStatus,
    tenantMismatch,
    clearTenantMismatch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
